import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { GET } from '../../config/api';
import EmailIcon from '@mui/icons-material/Email';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import lightningImage from '../../assets/images/lightning.png';
import styles from './LandingScreen.module.css';

const LandingScreen = () => {
  const navigate = useNavigate();
  const [configs, setConfigs] = useState(null);

  useEffect(() => {
    // Load configs to check if social login should be shown
    const loadConfigs = async () => {
      try {
        const response = await GET('/auth/configs');
        if (response.status === 200 && response.data?.configs) {
          setConfigs(response.data.configs);
          localStorage.setItem('tellmiz_configs', JSON.stringify(response.data.configs));
        }
      } catch (error) {
        console.error('Error loading configs:', error);
      }
    };
    loadConfigs();
  }, []);

  const handleLogin = () => {
    if (configs?.show_social_login) {
      navigate('/login'); // Social login route (to be implemented)
    } else {
      navigate('/login-email'); // Email login route
    }
  };

  const handleSignup = () => {
    navigate('/signup');
  };

  return (
    <div className={styles.container}>
      {/* Main background gradient */}
      <div className={styles.backgroundGradient}></div>

      {/* Electric background pulse */}
      <div className={styles.electricBackground}></div>

      {/* Thunder flash overlay */}
      <div className={styles.thunderFlash}></div>

      {/* Animated gradient overlay */}
      <div className={styles.gradientOverlay}></div>

      <div className={styles.content}>
        {/* Animated Logo */}
        <div className={styles.logoContainer}>
          <img src={lightningImage} alt="Lightning" className={styles.logoImage} />
          
          {/* Realistic Branching Lightning Bolts */}
          <div className={styles.lightningBolt1}>
            <div className={styles.lightningSegment} style={{ width: '4px', height: '80px', transform: 'rotate(5deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '3px', height: '60px', top: '70px', left: '8px', transform: 'rotate(-15deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '2px', height: '40px', top: '120px', left: '15px', transform: 'rotate(25deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '2px', height: '35px', top: '45px', left: '-5px', transform: 'rotate(-35deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '25px', top: '90px', left: '12px', transform: 'rotate(45deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '20px', top: '100px', left: '20px', transform: 'rotate(-25deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '15px', top: '55px', left: '-8px', transform: 'rotate(-55deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '18px', top: '110px', left: '25px', transform: 'rotate(15deg)' }}></div>
          </div>

          <div className={styles.lightningBolt2}>
            <div className={styles.lightningSegment} style={{ width: '3px', height: '90px', transform: 'rotate(-8deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '2px', height: '70px', top: '80px', left: '-6px', transform: 'rotate(20deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '2px', height: '50px', top: '140px', left: '2px', transform: 'rotate(-30deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '2px', height: '40px', top: '30px', left: '8px', transform: 'rotate(40deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '30px', top: '95px', left: '-8px', transform: 'rotate(-45deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '25px', top: '160px', left: '5px', transform: 'rotate(20deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '20px', top: '50px', left: '15px', transform: 'rotate(60deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '15px', top: '120px', left: '-10px', transform: 'rotate(-60deg)' }}></div>
          </div>

          <div className={styles.lightningBolt3}>
            <div className={styles.lightningSegment} style={{ width: '2px', height: '100px', transform: 'rotate(10deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '2px', height: '80px', top: '90px', left: '5px', transform: 'rotate(-25deg)' }}></div>
            <div className={styles.lightningSegment} style={{ width: '1px', height: '60px', top: '160px', left: '-2px', transform: 'rotate(35deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '35px', top: '60px', left: '8px', transform: 'rotate(50deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '25px', top: '130px', left: '3px', transform: 'rotate(-40deg)' }}></div>
            <div className={styles.lightningBranch} style={{ width: '1px', height: '18px', top: '80px', left: '12px', transform: 'rotate(70deg)' }}></div>
          </div>
        </div>

        {/* Animated Title and Subtitle */}
        <div className={styles.landingHead}>
          <div className={styles.titleContainer}>
            {Array.from('TELLMIZ').map((letter, index) => (
              <span 
                key={index} 
                className={styles.titleLetter}
                style={{ 
                  animationDelay: `${800 + index * 100}ms` 
                }}
              >
                {letter}
              </span>
            ))}
          </div>
          <p className={styles.subtitle}>We pave the way</p>
        </div>

        {/* Animated Actions Section */}
        <div className={styles.actions}>
          <p className={styles.description}>
            Sign up and take your student life to the next level.
          </p>

          {/* Animated Login Button */}
          <button 
            onClick={handleLogin}
            className={styles.loginButton}
          >
            <div className={styles.buttonContent}>
              <EmailIcon className={styles.buttonIcon} />
              <span className={styles.loginButtonText}>Log In</span>
            </div>
          </button>

          {/* Animated Signup Button */}
          <button 
            onClick={handleSignup}
            className={styles.signupButton}
          >
            <div className={styles.buttonContent}>
              <PersonAddIcon className={styles.buttonIcon} />
              <span className={styles.signupButtonText}>Sign Up</span>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LandingScreen;

